import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { resolveMediaUrl } from '../utils/url';
import './home.css';

function isSafariBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  const vendor = String(navigator.vendor || '');
  const isAppleVendor = /Apple/i.test(vendor);
  const looksLikeSafari = /Safari/i.test(ua) && /Version\//i.test(ua);
  const isOtherBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|Chrome|Chromium|Edg|Firefox|OPR/i.test(ua);
  return isAppleVendor && looksLikeSafari && !isOtherBrowser;
}

function parseTwitchChannel(link) {
  try {
    const u = new URL(link);
    if (u.hostname.endsWith('twitch.tv')) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0]) return parts[0];
    }
  } catch {
    /* ignore */
  }
  return '';
}

function parseYouTubeEmbed(link) {
  try {
    const u = new URL(link);
    const host = u.hostname.replace(/^www\./,'');
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtu.be') {
      // watch?v=ID
      const v = u.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}?autoplay=1&mute=1&playsinline=1&enablejsapi=1`;
      // youtu.be/ID
      if (host === 'youtu.be') {
        const id = u.pathname.split('/').filter(Boolean)[0];
        if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&playsinline=1&enablejsapi=1`;
      }
      // live link for channel: /channel/CHANNEL_ID/live
      const parts = u.pathname.split('/').filter(Boolean);
      const chIdx = parts.indexOf('channel');
      if (chIdx !== -1 && parts[chIdx+1]) {
        const channelId = parts[chIdx+1];
        return `https://www.youtube.com/embed/live_stream?channel=${channelId}&autoplay=1&mute=1&playsinline=1&enablejsapi=1`;
      }
    }
  } catch {
    /* ignore */
  }
  return '';
}

function parseYouTubeIds(link) {
  // Returns { videoId, channelId } where available for chat embedding
  try {
    const u = new URL(link);
    const host = u.hostname.replace(/^www\./,'');
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtu.be') {
      const v = u.searchParams.get('v');
      if (v) return { videoId: v, channelId: '' };
      if (host === 'youtu.be') {
        const id = u.pathname.split('/').filter(Boolean)[0];
        if (id) return { videoId: id, channelId: '' };
      }
      const parts = u.pathname.split('/').filter(Boolean);
      const chIdx = parts.indexOf('channel');
      if (chIdx !== -1 && parts[chIdx+1]) {
        return { videoId: '', channelId: parts[chIdx+1] };
      }
    }
  } catch {
    /* ignore */
  }
  return { videoId: '', channelId: '' };
}

// Load external scripts once
const _scriptPromises = new Map();
const loadScriptOnce = (src) => {
  if (!src) return Promise.reject(new Error('missing script src'));
  if (_scriptPromises.has(src)) return _scriptPromises.get(src);

  const existing = document.querySelector(`script[src='${src}']`);
  const p = new Promise((resolve, reject) => {
    const done = () => resolve();
    const fail = (e) => reject(e || new Error('script load failed'));

    // If global already present, consider it loaded.
    // (This avoids hanging when a script was injected earlier in another way.)
    if (/player\.twitch\.tv\/js\/embed\/v1\.js/i.test(src) && window.Twitch) return done();

    if (existing) {
      // If we can detect it already loaded, resolve; otherwise wait for onload.
      const alreadyLoaded = existing.dataset && existing.dataset.loaded === '1';
      if (alreadyLoaded) return done();
      existing.addEventListener('load', () => {
        try { existing.dataset.loaded = '1'; } catch {}
        done();
      }, { once: true });
      existing.addEventListener('error', fail, { once: true });
      return;
    }

    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = () => {
      try { s.dataset.loaded = '1'; } catch {}
      done();
    };
    s.onerror = fail;
    document.head.appendChild(s);
  });

  _scriptPromises.set(src, p);
  return p;
};

// Ensure Twitch player init doesn't stampede when multiple players mount at once.
let _twitchInitChain = Promise.resolve();
const enqueueTwitchInit = (fn) => {
  const run = () => Promise.resolve().then(fn);
  const next = _twitchInitChain.then(run, run);
  // keep chain alive even if one init fails
  _twitchInitChain = next.catch(() => {});
  return next;
};

function getEmbedParents() {
  const host = (typeof window !== 'undefined' ? window.location.hostname : '') || 'localhost';
  const alt = host.startsWith('www.') ? host.slice(4) : `www.${host}`;
  return Array.from(new Set([host, alt].filter(Boolean)));
}

function createLurkerSessionId() {
  try {
    const key = 'lurker_session_id';
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`).slice(0, 64);
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`.slice(0, 64);
  }
}

async function logLurkerEvent(payload) {
  try {
    const sessionId = createLurkerSessionId();
    const body = {
      sessionId,
      hostname: (typeof window !== 'undefined' ? window.location.hostname : '') || 'localhost',
      path: (typeof window !== 'undefined' ? window.location.pathname : '') || '',
      userAgent: (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '',
      ...payload,
    };
    // Server decides based on settings (enabled/mode) whether to persist.
    await api.post('/lurker-logs/ingest', body).catch(() => {});
  } catch {
    /* ignore */
  }
}

function TwitchPlayer({ channel, safariSafe = false }) {
  const mountRef = useRef(null);
  const playerRef = useRef(null);
  const mountId = useMemo(() => `twitch-player-${Math.random().toString(36).slice(2)}`, []);
  useEffect(() => {
    let mounted = true;
    if (!channel) return;
    const run = async () => {
      try {
        await enqueueTwitchInit(async () => {
          await logLurkerEvent({ platform: 'twitch', event: 'TWITCH_SCRIPT_LOAD_START', severity: 'info', message: `channel=${channel}` });
          await loadScriptOnce('https://player.twitch.tv/js/embed/v1.js');
          await logLurkerEvent({ platform: 'twitch', event: 'TWITCH_SCRIPT_LOAD_OK', severity: 'info', message: `channel=${channel}` });
          const mountEl = mountRef.current;
          if (!mounted || !mountEl) return;
          if (!window.Twitch || !window.Twitch.Player) throw new Error('Twitch embed not available');

          const parents = getEmbedParents();
          // Destroy old
          if (playerRef.current && typeof playerRef.current.destroy === 'function') {
            try { playerRef.current.destroy(); } catch { /* ignore */ }
            playerRef.current = null;
          }
          // Create player
          // Safari can be pickier with embed initialization; using an element id tends to be more compatible.
          const target = safariSafe ? mountId : mountEl;
          await logLurkerEvent({ platform: 'twitch', event: 'TWITCH_PLAYER_CREATE', severity: 'info', message: `channel=${channel}`, details: { parents, safariSafe: !!safariSafe } });
          playerRef.current = new window.Twitch.Player(target, {
            channel,
            parent: parents,
            autoplay: safariSafe ? false : true,
            muted: safariSafe ? true : false,
            width: '100%',
            height: '100%',
          });

          // If READY never fires (blocked by browser/extension), fall back to iframe.
          let readyFired = false;
          const readyTimeout = setTimeout(() => {
            if (!mounted || readyFired) return;
            logLurkerEvent({ platform: 'twitch', event: 'TWITCH_READY_TIMEOUT', severity: 'warn', message: `channel=${channel}`, details: { parents, safariSafe: !!safariSafe } }).catch(() => {});
            throw new Error('Twitch player READY timeout');
          }, 6000);

          playerRef.current.addEventListener(window.Twitch.Player.READY, () => {
            readyFired = true;
            clearTimeout(readyTimeout);
            logLurkerEvent({ platform: 'twitch', event: 'TWITCH_READY', severity: 'info', message: `channel=${channel}`, details: { parents, safariSafe: !!safariSafe } }).catch(() => {});
            try {
              if (!safariSafe) {
                if (typeof playerRef.current.setVolume === 'function') playerRef.current.setVolume(0.05);
                if (typeof playerRef.current.play === 'function') playerRef.current.play();
              }
            } catch {
              /* ignore */
            }
          });
        });
      } catch {
        // fallback to simple iframe
        const parents = getEmbedParents();
        const parentParam = encodeURIComponent(parents[0] || 'localhost');
        const url = safariSafe
          ? `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${parentParam}&autoplay=false&muted=true`
          : `https://player.twitch.tv/?channel=${encodeURIComponent(channel)}&parent=${parentParam}&autoplay=true`;
        if (mountRef.current) {
          logLurkerEvent({ platform: 'twitch', event: 'TWITCH_FALLBACK_IFRAME', severity: 'warn', message: `channel=${channel}`, details: { parent: parents[0] || 'localhost', safariSafe: !!safariSafe } }).catch(() => {});
          mountRef.current.innerHTML = `<iframe src="${url}" allow="autoplay; fullscreen" frameborder="0" allowfullscreen style="position:absolute;inset:0;width:100%;height:100%;border:0"></iframe>`;
        }
      }
    };
    run();
    return () => {
      mounted = false;
      const mountEl = mountRef.current;
      if (playerRef.current && typeof playerRef.current.destroy === 'function') { 
        try { playerRef.current.destroy(); } catch { /* ignore */ }
      }
      playerRef.current = null;
      if (mountEl) mountEl.innerHTML = '';
    };
  }, [channel, mountId, safariSafe]);
  return (
    <div style={{ position:'relative', width:'100%', borderRadius:8, overflow:'hidden', background:'#0b1220', paddingTop:'56.25%' }}>
      <div ref={mountRef} id={mountId} style={{ position:'absolute', inset:0, width:'100%', height:'100%' }} />
    </div>
  );
}

function YouTubePlayer({ link }) {
  const origin = (typeof window !== 'undefined' && window.location.origin) ? window.location.origin : '';
  const base = useMemo(() => parseYouTubeEmbed(link), [link]);
  const src = useMemo(() => {
    if (!base) return '';
    const url = new URL(base);
    if (origin) url.searchParams.set('origin', origin);
    // Autoplay policies: start muted; user can unmute manually.
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('mute', '1');
    url.searchParams.set('playsinline', '1');
    url.searchParams.set('enablejsapi', '1');
    return url.toString();
  }, [base, origin]);
  if (!src) return null;
  return (
    <div style={{ position:'relative', width:'100%', borderRadius:8, overflow:'hidden', background:'#0b1220', paddingTop:'56.25%' }}>
      <iframe
        title="youtube-player"
        src={src}
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        frameBorder="0"
        style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:0 }}
      />
    </div>
  );
}

function KickPlayer({ link, safariSafe = false }) {
  // Expecting https://kick.com/{channel} or player.kick.com/{channel}
  let channel = '';
  try {
    const u = new URL(link);
    if (u.hostname.endsWith('kick.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0]) channel = parts[0];
    }
  } catch {
    /* ignore */
  }
  if (!channel) return null;
  const src = `https://player.kick.com/${encodeURIComponent(channel)}?autoplay=true`;
  if (!safariSafe) {
    return (
      <iframe
        title={`kick-${channel}`}
        src={src}
        allow="autoplay; fullscreen; picture-in-picture"
        frameBorder="0"
        allowFullScreen
        style={{ width:'100%', aspectRatio:'16/9', borderRadius:8 }}
      />
    );
  }
  // Safari-safe sizing (older Safari versions may not honor CSS aspect-ratio on iframes)
  return (
    <div style={{ position:'relative', width:'100%', borderRadius:8, overflow:'hidden', background:'#0b1220', paddingTop:'56.25%' }}>
      <iframe
        title={`kick-${channel}`}
        src={src}
        allow="autoplay; fullscreen; picture-in-picture"
        frameBorder="0"
        allowFullScreen
        style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:0 }}
      />
    </div>
  );
}

function FacebookPlayer({ link, safariSafe = false }) {
  // Use FB video plugin; needs a video/live URL; volume control not supported
  const src = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(link)}&show_text=false&autoplay=true`;
  if (!safariSafe) {
    return (
      <iframe
        title={`fb-${link}`}
        src={src}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
        frameBorder="0"
        allowFullScreen
        style={{ width:'100%', aspectRatio:'16/9', borderRadius:8 }}
      />
    );
  }
  // Safari-safe sizing (older Safari versions may not honor CSS aspect-ratio on iframes)
  return (
    <div style={{ position:'relative', width:'100%', borderRadius:8, overflow:'hidden', background:'#0b1220', paddingTop:'56.25%' }}>
      <iframe
        title={`fb-${link}`}
        src={src}
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
        frameBorder="0"
        allowFullScreen
        style={{ position:'absolute', inset:0, width:'100%', height:'100%', border:0 }}
      />
    </div>
  );
}

function TwitchChat({ channel }) {
  if (!channel) return null;
  const parents = getEmbedParents();
  const src = `https://www.twitch.tv/embed/${encodeURIComponent(channel)}/chat?parent=${encodeURIComponent(parents[0] || 'localhost')}&darkpopout&framed`; 
  return (
    <iframe
      title={`twitch-chat-${channel}`}
      src={src}
      frameBorder="0"
      style={{ width:'100%', height:'100%', border:0 }}
    />
  );
}

function YouTubeChat({ link }) {
  const { videoId, channelId } = useMemo(() => parseYouTubeIds(link || ''), [link]);
  const domain = (typeof window !== 'undefined' ? window.location.hostname : 'localhost') || 'localhost';
  // YouTube live chat requires either a videoId for a current live, or an embed with channelId (less reliable)
  let src = '';
  if (videoId) {
    src = `https://www.youtube.com/live_chat?v=${encodeURIComponent(videoId)}&embed_domain=${encodeURIComponent(domain)}`;
  } else if (channelId) {
    // Fallback: might not work if the channel is not live or chat disabled
    src = `https://www.youtube.com/live_chat?is_popout=1&embed_domain=${encodeURIComponent(domain)}&channel=${encodeURIComponent(channelId)}`;
  }
  if (!src) return null;
  return (
    <iframe
      title={`yt-chat-${videoId || channelId}`}
      src={src}
      frameBorder="0"
      style={{ width:'100%', height:'100%', border:0 }}
      allow="autoplay"
    />
  );
}

function KickChat({ link }) {
  // Unofficial: Kick chat embed endpoint (may change). If blocked by X-Frame-Options, we fallback to link.
  let channel = '';
  try {
    const u = new URL(link);
    if (u.hostname.endsWith('kick.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts[0]) channel = parts[0];
    }
  } catch {
    /* ignore */
  }
  if (!channel) return null;
  const parent = (typeof window !== 'undefined' ? window.location.hostname : 'localhost') || 'localhost';
  const src = `https://kick.com/embed/chat/${encodeURIComponent(channel)}?parent=${encodeURIComponent(parent)}`;
  return (
    <iframe
      title={`kick-chat-${channel}`}
      src={src}
      frameBorder="0"
      style={{ width:'100%', height:'100%', border:0 }}
    />
  );
}

export default function Lurker() {
  const safariSafe = useMemo(() => isSafariBrowser(), []);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showChat, setShowChat] = useState(() => {
    try { return localStorage.getItem('lurker_show_chat') === '1'; } catch { return false; }
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError('');
      try {
        const { data } = await api.get('/supportplan/public/live-streams');
        if (!alive) return;
        const now = Date.now();
        const entries = (data?.entries || []).filter(e => {
          const s = new Date(e.start).getTime();
          const en = new Date(e.end).getTime();
          return s <= now && en > now;
        });
        setItems(entries);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.error || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    const iv = setInterval(() => {
      // Refresh list every 5 minutes
      api.get('/supportplan/public/live-streams').then(({data}) => {
        const now = Date.now();
        const entries = (data?.entries || []).filter(e => {
          const s = new Date(e.start).getTime();
          const en = new Date(e.end).getTime();
          return s <= now && en > now;
        });
        setItems(entries);
      }).catch(() => { /* ignore */ });
    }, 300000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  useEffect(() => {
    try { localStorage.setItem('lurker_show_chat', showChat ? '1' : '0'); } catch { /* ignore */ }
  }, [showChat]);

  return (
    <div className="home-page">
      <section style={{ width:'100vw', margin:0, padding:0, position:'relative', left:'-120px' }}>
        <h2 className="hero-title" style={{ textAlign:'center', margin:'0 0 8px' }}>Lurker</h2>
        <p className="hero-desc" style={{ margin:'0 auto 24px', textAlign: 'center', maxWidth:760 }}>
          Automatisch gestartete Player für alle aktuell laufenden Streams aus dem Streamplan (sehr leise, nicht stumm). Optional mit Chat.
          ACHTUNG, bei den hier geöffneten Streams wirst du nicht immer auch wirklich als aktiver Zuschauer von der jeweiligen Plattform erkannt!
          Wenn du den Creator wirklich unterstützen willst, öffne den Stream im Browser oder nutze unseren "AP-N Lurker".
        </p>
      </section>
  <section style={{ width:'100%', maxWidth: 1600, margin:'0 auto', padding:'0 16px 40px 16px', color:'#fff' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          {error && <div style={{color:'tomato'}}>{error}</div>}
          {safariSafe && (
            <div style={{ marginLeft: error ? 12 : 0, marginRight: 12, opacity: 0.9, fontSize: 13 }}>
              <span style={{ display:'inline-flex', alignItems:'center', padding:'4px 10px', borderRadius: 999, border:'1px solid rgba(255,255,255,0.25)', background:'rgba(255,255,255,0.06)' }}>
                Safari‑Safe‑Mode aktiv
              </span>
            </div>
          )}
          <label style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto' }}>
            <input type="checkbox" checked={showChat} onChange={e => setShowChat(e.target.checked)} />
            Chat anzeigen
          </label>
        </div>
        {loading && <div>Lade ...</div>}
        {!loading && items.length === 0 && <div>Derzeit läuft kein Stream.</div>}
  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(520px, 1fr))', gap:16 }}>
          {items.map((it) => {
            const isTwitch = (it.platform || '').toLowerCase() === 'twitch' || /twitch\.tv/.test(it.streamLink || '');
            const isYouTube = (it.platform || '').toLowerCase() === 'youtube' || /youtu(be)?\.com/.test(it.streamLink || '');
            const isKick = (it.platform || '').toLowerCase() === 'kick' || /kick\.com/.test(it.streamLink || '');
            const isFacebook = (it.platform || '').toLowerCase().includes('facebook') || /facebook\.com|fb\.watch/.test(it.streamLink || '');
            const channel = isTwitch ? parseTwitchChannel(it.streamLink || '') : '';
            return (
              <div key={it.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, padding:12 }}>
                {/* Background watch pinger for YouTube entries to award time-based points */}
                {isYouTube ? <WatchPinger entryId={it.id} platform="YouTube" /> : null}
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  {it.avatarUrl ? (<img src={resolveMediaUrl(it.avatarUrl)} alt="avatar" style={{ width:28, height:28, borderRadius:'50%', objectFit:'cover' }} />) : <div style={{width:28,height:28,borderRadius:'50%',background:'#222'}} />}
                  <div style={{ fontWeight:600 }}>{it.userDisplayName || 'Streamer'}</div>
                </div>
                <div style={{ display:'grid', gridTemplateColumns: showChat ? '2fr 1fr' : '1fr', gap:12, alignItems:'stretch' }}>
                  <div>
                    {isTwitch && channel && <TwitchPlayer channel={channel} safariSafe={safariSafe} />}
                    {!isTwitch && isYouTube && <YouTubePlayer link={it.streamLink} />}
                    {!isTwitch && !isYouTube && isKick && <KickPlayer link={it.streamLink} safariSafe={safariSafe} />}
                    {!isTwitch && !isYouTube && !isKick && isFacebook && <FacebookPlayer link={it.streamLink} safariSafe={safariSafe} />}
                    {!isTwitch && !isYouTube && !isKick && !isFacebook && (
                      <div style={{ padding:12, borderRadius:8, background:'#0b1220', fontSize:12 }}>
                        Für diese Plattform ist aktuell keine sichere Einbettung verfügbar. <a href={it.streamLink} target="_blank" rel="noreferrer">Direkt öffnen</a>
                      </div>
                    )}
                  </div>
                  {showChat && (
                    <div style={{ minWidth: 280, height: '100%' }}>
                      <div style={{ position:'relative', width:'100%', height:'100%', borderRadius:8, overflow:'hidden', background:'#0b1220', minHeight: 300 }}>
                        {isTwitch && channel && <TwitchChat channel={channel} />}
                        {!isTwitch && isYouTube && <YouTubeChat link={it.streamLink} />}
                        {!isTwitch && !isYouTube && isKick && <KickChat link={it.streamLink} />}
                        {!isTwitch && !isYouTube && !isKick && isFacebook && (
                          <div style={{ padding:12, fontSize:12 }}>Facebook-Chat kann nicht eingebettet werden. <a href={it.streamLink} target="_blank" rel="noreferrer">Zur Live-Seite</a></div>
                        )}
                        {!isTwitch && !isYouTube && !isKick && !isFacebook && (
                          <div style={{ padding:12, fontSize:12 }}>Kein Chat-Embed verfügbar.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ marginTop:8, opacity:.9 }}>
                  <div style={{ fontWeight:600 }}>{it.title || ''}</div>
                  <div style={{ fontSize:13, opacity:.8 }}>{it.description || ''}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function WatchPinger({ entryId, platform }) {
  useEffect(() => {
    let alive = true;
    const send = async () => {
      try { await api.post('/users/watch/ping', { entryId, platform }); } catch { /* ignore */ }
    };
    // initial ping
    send();
    const id = setInterval(() => { if (alive) send(); }, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [entryId, platform]);
  return null;
}
